<script setup lang="ts">
import type { SocketMessage, Sticker } from "~/types";
import { repository } from "~/repository";
import { useAuthStore } from "~/stores/auth/store";
import ChatMessage from "~/components/Chat/ChatMessage.vue";

const props = defineProps({
  message: {
    type: Object as PropType<SocketMessage>,
    required: true,
  },
  stickers: {
    type: Object as PropType<Sticker[]>,
    required: true,
  },
  videoId: {
    type: String,
    required: true,
  },
});

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const emit = defineEmits<{
  (e: "blockUser", sender: string): void;
}>();

const toast = useToast();

function getDisplayUsername(item: SocketMessage) {
  if (
    ["moderator", ""].includes(item.content.code) ||
    item.content.msg_type === "system"
  ) {
    if (item.sender.length > 20) return `${item.sender.slice(0, 20)}..`;
    return item.sender;
  }
  return "";
}

function isSticker(text: string) {
  return text.startsWith("sticker:");
}

function getStickerURL(text: string) {
  const url = props.stickers.find(
    ({ sticker_id }) => sticker_id === text.slice(8),
  );
  if (url) {
    return url.value;
  } else return text;
}

const reportMenu = ref();
const reportMenuItems = ref([
  {
    label: `Report ${props.message.sender}`,
    icon: "fas fa-flag",
    command: async () => {
      await handleReportUser();
    },
  },
  {
    label: `Block ${props.message.sender}`,
    icon: "fas fa-ban",
    command: () => {
      emit("blockUser", props.message.sender);
    },
  },
]);

const toggleReportMenu = (event: any) => {
  reportMenu.value.toggle(event);
};

async function handleReportUser() {
  const payload = {
    videoId: props.videoId,
    messageId: props.message.id,
    username: props.message.sender,
  };

  try {
    await repository.chat.reportParticipant(payload);

    toast.add({
      severity: "warn",
      summary: "User reported !",
      detail: `User ${props.message.sender} has been reported`,
      life: 3000,
    });
  } catch (e) {
    handleApiError(e);
  }
}

function getUsernameColor(message: SocketMessage) {
  if (
    message.content.code === "admin" ||
    message.content.code === "moderator" ||
    message.sender.indexOf("*") > -1
  ) {
    return "#ff760d";
  }
  if (message.sender === "PokerGO") {
    return "#FDDC9B";
  }

  if (message.sender === "PokerGO") {
    return "#FDDC9B";
  }
  // Create a hash from the username string

  const username = message.sender;
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colorPallet = [
    "#e91e63", // Pink
    "#2196f3", // Blue
    "#ff9800", // Orange
    "#4caf50", // Green
    "#9c27b0", // Purple
    "#00bcd4", // Cyan
    "#f44336", // Red
    "#ffeb3b", // Yellow
    "#673ab7", // Deep Purple
    "#009688", // Teal
  ];

  return colorPallet[Math.abs(hash) % colorPallet.length];
}
</script>

<template>
  <div
    :class="[
      'flex gap-2 rounded-md bg-surface-bg px-3 py-2 text-[13px]',
      { '!bg-[#F9D999]/30': message.sender === 'PokerGO' },
    ]"
  >
    <div class="flex-grow overflow-hidden">
      <span
        :style="{ color: getUsernameColor(message) }"
        :class="['whitespace-nowrap pr-2 text-primary-200']"
      >
        {{ getDisplayUsername(message) }}
      </span>
      <span v-if="isSticker(message.content.message)">
        <img
          alt="sticker"
          class="inline h-[50px] w-[50px]"
          :src="getStickerURL(message.content.message)"
        />
      </span>
      <span v-else class="break-words">
        {{ message.content.message }}
      </span>
    </div>
    <div
      v-if="
        !(
          message.sender === user.username ||
          message.sender === 'PokerGO' ||
          message.content.code === 'admin' ||
          message.content.code === 'moderator'
        )
      "
    >
      <span @click="toggleReportMenu">
        <font-awesome-icon
          class="cursor-pointer"
          :icon="['fas', 'ellipsis-vertical']"
        />
      </span>
      <Menu
        id="overlay_menu"
        ref="reportMenu"
        :model="reportMenuItems"
        :popup="true"
      >
        <template #itemicon="item">
          <font-awesome-icon :icon="item.item.icon" class="mr-2" />
        </template>
      </Menu>
    </div>
  </div>
</template>
